import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Divider,
  CircularProgress,
  Alert,
  AlertTitle,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Container
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  CreditCard as CreditCardIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ShoppingCart as ShoppingCartIcon,
  History as HistoryIcon,
  AccessTime as AccessTimeIcon,
  Lock as LockIcon,
  Info as InfoIcon,
  LockOpen as LockOpenIcon,
  Phone as PhoneIcon,
  Description as DocumentIcon,
  Help as HelpIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Gavel as GavelIcon,
  Visibility as VisibilityIcon,
  Timeline as TimelineIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import DataManagement from './DataManagement';
import PublicPrivacy from './PublicPrivacy';
import { Link as RouterLink } from 'react-router-dom';
import TermsOfService from './TermsOfService';
import UserGuide from './UserGuide';
import FAQPage from './FAQPage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-hot-toast';
import { isUserAdmin } from '../utils/adminUtils';

const Profile = () => {
  const { currentUser } = useAuth();
  const { isPremium, subscriptionDetails, isLoading, error, upgradeToPremuim, checkSubscription } = useSubscription();
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'upgrade' | 'manage' | 'success'>('upgrade');
  const [activeSection, setActiveSection] = useState<'subscription' | 'userGuide' | 'privacy' | 'dataManagement' | 'faq' | 'terms'>('subscription');
  const [isAdmin, setIsAdmin] = useState(false);

  // Add useEffect to check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      const adminStatus = await isUserAdmin();
      setIsAdmin(adminStatus);
    };
    
    checkAdminStatus();
  }, []);

  if (!currentUser) {
    return (
      <Alert severity="error">
        You must be logged in to view your profile.
      </Alert>
    );
  }

  const handleUpgrade = async () => {
    try {
      setUpgradeLoading(true);
      await upgradeToPremuim();
      // Show success dialog
      setDialogType('upgrade');
      setShowDialog(true);
    } catch (err) {
      console.error('Error upgrading:', err);
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleVerifySubscription = async () => {
    try {
      setUpgradeLoading(true);
      
      // Add a debounce mechanism to prevent multiple rapid clicks
      const now = Date.now();
      const lastVerification = parseInt(localStorage.getItem('lastManualVerification') || '0', 10);
      const timeSinceLastVerification = now - lastVerification;
      
      if (timeSinceLastVerification < 5000) { // 5 seconds cooldown
        toast.info('Please wait before trying to verify again', {
          position: 'bottom-center',
          autoClose: 3000
        });
        setUpgradeLoading(false);
        return;
      }
      
      // Store current verification time
      localStorage.setItem('lastManualVerification', now.toString());
      
      // First, try to check subscription directly with force flag to bypass any caching
      const result = await checkSubscription(true);
      
      // If the check was successful and user is premium, show success message
      if (result && isPremium) {
        setDialogType('success');
        setShowDialog(true);
        toast.success('Subscription verified successfully!', {
          position: 'bottom-center',
          autoClose: 3000
        });
        return;
      }
      
      // If check failed or user is not premium, try a more thorough check with the cloud function
      const functions = getFunctions();
      const verifySubscriptionFunc = httpsCallable(functions, 'verifySubscription');
      
      // Add a toast message to indicate verification is in progress
      toast.info('Verifying subscription status with server...', {
        position: 'bottom-center',
        autoClose: 3000
      });
      
      const response = await verifySubscriptionFunc();
      const data = response.data as any;
      
      console.log('Manual verification result:', data);
      
      // Force a refresh of the subscription context after cloud function call
      await checkSubscription(true);
      
      // Show appropriate message based on verification result
      if (data.isActive) {
        toast.success('Subscription verified successfully!', {
          position: 'bottom-center',
          autoClose: 3000
        });
        setDialogType('success');
        setShowDialog(true);
      } else {
        // Handle different subscription states with appropriate messages
        if (data.subscription?.status === 'expired') {
          toast.warning('Your subscription has expired. Please renew to continue using premium features.', {
            position: 'bottom-center',
            autoClose: 5000
          });
        } else if (data.subscription?.status === 'paused') {
          toast.warning('Your subscription is currently paused. Please resume your subscription to use premium features.', {
            position: 'bottom-center',
            autoClose: 5000
          });
        } else if (data.subscription?.status === 'refunded') {
          toast.warning('Your subscription has been refunded. Please subscribe again to use premium features.', {
            position: 'bottom-center',
            autoClose: 5000
          });
        } else if (data.subscription === null || !data.subscription || data.message === 'No subscription found') {
          // Free user with no subscription path - show mobile app subscription message
          const message = 'Please subscribe using the Amsler Grid Daily app on your iOS or Android device to access premium features. Your subscription will automatically sync across all your devices using the same account.';
          toast.info(message, {
            position: 'bottom-center',
            autoClose: 7000
          });
          // Update the error state to display the message in the UI
          upgradeToPremuim(); // This will set the appropriate error message
        } else if (data.testCount !== undefined) {
          // Free user
          const remainingTests = 10 - data.testCount;
          toast.info(`You are using the free plan. ${remainingTests > 0 ? `You have ${remainingTests} tests remaining.` : 'You have reached your free test limit.'}`, {
            position: 'bottom-center',
            autoClose: 5000
          });
        } else {
          toast.warning('No active subscription found.', {
            position: 'bottom-center',
            autoClose: 3000
          });
        }
      }
    } catch (err) {
      console.error('Error verifying subscription:', err);
      
      // Check if this is a free user with no subscription path
      if (err instanceof Error && 
          (err.message.includes('No subscription found') || 
           err.message.includes('User not found') ||
           err.message.includes('subscription is null'))) {
        
        const message = 'Please subscribe using the Amsler Grid Daily app on your iOS or Android device to access premium features. Your subscription will automatically sync across all your devices using the same account.';
        toast.info(message, {
          position: 'bottom-center',
          autoClose: 7000
        });
        
        // Set the error message directly instead of calling upgradeToPremuim
        upgradeToPremuim(); // This will set the appropriate error message
      } else {
        // For other types of errors, show the generic error message
        toast.error('Verification failed. Please try again later.', {
          position: 'bottom-center',
          autoClose: 3000
        });
      }
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleOpenManage = () => {
    setDialogType('manage');
    setShowDialog(true);
  };

  const formatDate = (date: Date | null | undefined) => {
    if (!date) return 'N/A';
    return format(date, 'MMM d, yyyy');
  };
  
  const getStatusColor = (status: string | undefined, cancelAtPeriodEnd?: boolean) => {
    if (cancelAtPeriodEnd) {
      return 'warning';
    }

    switch (status) {
      case 'active':
        return 'success';
      case 'trial':
        return 'info';
      case 'canceled':
        return 'warning';
      case 'past_due':
      case 'unpaid':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderRightPanel = () => {
    switch (activeSection) {
      case 'userGuide':
        return (
          <Paper sx={{ p: 3, height: '100%', overflow: 'auto' }}>
            <UserGuide />
          </Paper>
        );
      case 'terms':
        return <TermsOfService />;
      case 'privacy':
        return <PublicPrivacy />;
      case 'dataManagement':
        return <DataManagement />;
      case 'faq':
        return (
          <Paper sx={{ p: 3, height: '100%', overflow: 'auto' }}>
            <FAQPage />
          </Paper>
        );
      default:
        return (
          <Card>
            <CardContent>
              {isLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography>Verifying subscription...</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    (This should only take a few seconds)
                  </Typography>
                </Box>
              ) : error && typeof error === 'string' && error.includes('Subscribe using') ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      startIcon={<PhoneIcon />}
                      onClick={() => window.open('https://apps.apple.com/app/amslergriddaily', '_blank')}
                      sx={{ mr: 2 }}
                    >
                      App Store
                    </Button>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      startIcon={<ShoppingCartIcon />}
                      onClick={() => window.open('https://play.google.com/store/apps/details?id=com.amslergriddaily', '_blank')}
                    >
                      Google Play
                    </Button>
                  </Box>
                </Box>
              ) : isPremium ? (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Subscription Details</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {subscriptionDetails?.cancelAtPeriodEnd && (
                        <Chip 
                          label="CANCELED" 
                          color="warning"
                          size="small" 
                        />
                      )}
                    {subscriptionDetails?.status && (
                      <Chip 
                        label={subscriptionDetails.status.toUpperCase()} 
                          color={getStatusColor(subscriptionDetails.status, subscriptionDetails.cancelAtPeriodEnd)}
                        size="small" 
                      />
                    )}
                    </Box>
                  </Box>
                  
                  <Divider sx={{ mb: 2 }} />
        
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CalendarIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            Start Date
                          </Typography>
                          <Typography variant="body1">
                            {formatDate(subscriptionDetails?.periodStart)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AccessTimeIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {subscriptionDetails?.cancelAtPeriodEnd ? 'Expiration Date' : 'Next Billing Date'}
                          </Typography>
                          <Typography variant="body1">
                            {formatDate(subscriptionDetails?.periodEnd)}
                          </Typography>
                          {subscriptionDetails?.autoRenewing && subscriptionDetails?.nextRenewalDate && (
                            <Typography variant="caption" color="textSecondary">
                              Auto-renews on {formatDate(subscriptionDetails.nextRenewalDate)}
                            </Typography>
                          )}
                          {subscriptionDetails?.cancelAtPeriodEnd && (
                            <Typography variant="caption" color="warning.main">
                              Subscription will end on this date
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CreditCardIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            Plan
                          </Typography>
                          <Typography variant="body1">
                            {subscriptionDetails?.planId || 'Monthly Subscription'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <HistoryIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            Last Verified
                          </Typography>
                          <Typography variant="body1">
                            {subscriptionDetails?.lastVerified 
                              ? format(subscriptionDetails.lastVerified, 'MMM d, yyyy h:mm a')
                              : 'Not verified yet'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    
                    {/* Platform-specific receipt details */}
                    {subscriptionDetails?.platform && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          {subscriptionDetails.platform.charAt(0).toUpperCase() + subscriptionDetails.platform.slice(1)} Platform Details:
                        </Typography>
                        
                        {subscriptionDetails.platform === 'ios' || subscriptionDetails.platform === 'macos' ? (
                          subscriptionDetails.appleReceipt ? (
                            <List dense>
                              <ListItem>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <InfoIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary="Product ID" 
                                  secondary={subscriptionDetails.appleReceipt.productId || 'Unknown'}
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <InfoIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary="Subscription Environment" 
                                  secondary={subscriptionDetails.appleReceipt.environment || 'Production'}
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <InfoIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary="Auto-Renew Status" 
                                  secondary={subscriptionDetails.appleReceipt.autoRenewStatus ? 'Active' : 'Disabled'}
                                />
                              </ListItem>
                            </List>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              No {subscriptionDetails.platform} receipt details available
                            </Typography>
                          )
                        ) : subscriptionDetails.platform === 'android' ? (
                          subscriptionDetails.googlePlayReceipt ? (
                            <List dense>
                              <ListItem>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <InfoIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary="Product ID" 
                                  secondary={subscriptionDetails.googlePlayReceipt.productId || 'Unknown'}
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <InfoIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary="Package Name" 
                                  secondary={subscriptionDetails.googlePlayReceipt.packageName}
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <InfoIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary="Auto-Renewing" 
                                  secondary={subscriptionDetails.googlePlayReceipt.autoRenewing ? 'Active' : 'Disabled'}
                                />
                              </ListItem>
                            </List>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              No Google Play receipt details available
                            </Typography>
                          )
                        ) : subscriptionDetails.platform === 'web' ? (
                          subscriptionDetails.webReceipt ? (
                            <List dense>
                              <ListItem>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <InfoIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary="Payment Method" 
                                  secondary={`${subscriptionDetails.webReceipt.paymentMethodType || 'Credit'} Card`}
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <InfoIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary="Subscription ID" 
                                  secondary={subscriptionDetails.webReceipt.subscriptionId.substring(0, 8) + '...'}
                                />
                              </ListItem>
                            </List>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              No web payment details available
                            </Typography>
                          )
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Unknown platform
                          </Typography>
                        )}
                        
                        {/* Verification status */}
                        {subscriptionDetails.lastVerificationResult && (
                          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>
                              Verification Status:
                            </Typography>
                            <Chip 
                              size="small"
                              label={subscriptionDetails.lastVerificationResult.toUpperCase()}
                              color={
                                subscriptionDetails.lastVerificationResult === 'success' ? 'success' :
                                subscriptionDetails.lastVerificationResult === 'failed' ? 'error' : 'default'
                              }
                            />
                          </Box>
                        )}
                      </Grid>
                    )}
                  </Grid>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Subscription Benefits:
                  </Typography>
                  
                  <List dense>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Unlimited Amsler Grid tests" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Complete test history" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Download and share results" />
                    </ListItem>
                  </List>
                  
                  {subscriptionDetails?.cancelAtPeriodEnd && (
                    <Alert 
                      severity="warning" 
                      sx={{ mt: 1, mb: 2 }}
                    >
                      <AlertTitle>Subscription Canceled</AlertTitle>
                      Your subscription has been canceled but will remain active until {formatDate(subscriptionDetails?.periodEnd)}.
                      No further charges will be made.
                    </Alert>
                  )}
                </Box>
              ) : (
                <Box>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <LockIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                      Subscribe to Unlock All Features
                    </Typography>
                    <Typography variant="body1" color="textSecondary" paragraph>
                      Get unlimited access to Amsler Grid tests and track your eye health over time.
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ mb: 3 }} />
                  
                  <Box sx={{ 
                    backgroundColor: 'rgba(0, 229, 255, 0.05)',
                    border: '1px solid rgba(0, 229, 255, 0.2)',
                    borderRadius: 2,
                    p: 3,
                    mb: 3
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PhoneIcon color="primary" sx={{ mr: 2 }} />
                      <Typography variant="h6">
                        Subscribe on your mobile device
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" paragraph>
                      For legal compliance with app store policies, please subscribe using the
                      Amsler Grid Daily app on your iOS or Android device. Web subscriptions will
                      be available in a future update.
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={6}>
                        <Button 
                          variant="outlined" 
                          color="primary" 
                          fullWidth
                          startIcon={<Box component="span" sx={{ fontWeight: 'bold', fontSize: 18 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.32 2.12-1.66 4.1-3.74 4.25z" />
                            </svg>
                          </Box>}
                          sx={{ py: 1.5 }}
                        >
                          iOS App
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Button 
                          variant="outlined" 
                          color="primary" 
                          fullWidth
                          startIcon={<Box component="span" sx={{ fontWeight: 'bold', fontSize: 18 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.523 15.34l-.001-.001 2.845 4.832a.5.5 0 01-.432.75h-2.839a.75.75 0 01-.648-.373l-2.87-4.88c-.376.341-.795.61-1.246.806a3.73 3.73 0 01-1.477.294h-3.33a3.75 3.75 0 01-3.75-3.75V7.5a3.75 3.75 0 013.75-3.75h3.33c.51 0 .997.095 1.447.285.451.19.854.462 1.204.811a3.62 3.62 0 011.069 2.653 3.62 3.62 0 01-1.07 2.654c.025.026.049.053.072.08l2.797 4.761.002-.002a.127.127 0 00.174.046.127.127 0 00.046-.174l-2.798-4.762A3.62 3.62 0 0117.523 7.5a3.62 3.62 0 01-1.07-2.654 3.62 3.62 0 011.07-2.654A3.62 3.62 0 0120 3.75h.25v7.5H20a3.62 3.62 0 01-2.477-1.01z" />
                            </svg>
                          </Box>}
                          sx={{ py: 1.5 }}
                        >
                          Android App
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 2 }}>
                    Already subscribed? Please contact support if you believe this is an error.
                  </Typography>
                </Box>
              )}
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
              {isPremium ? (
                <>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={handleVerifySubscription}
                    disabled={upgradeLoading}
                    sx={{ mr: 1 }}
                  >
                    {upgradeLoading ? <CircularProgress size={20} /> : 'Verify Subscription'}
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      component={RouterLink}
                      to="/subscription-debugger"
                      sx={{ mr: 1 }}
                    >
                      Debug Subscription
                    </Button>
                  )}
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleOpenManage}
                  >
                    Manage Subscription
                  </Button>
                </>
              ) : (
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={handleVerifySubscription}
                  disabled={upgradeLoading}
                >
                  {upgradeLoading ? <CircularProgress size={20} /> : 'Verify Subscription Status'}
                </Button>
              )}
            </CardActions>
          </Card>
        );
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Your Profile
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Account Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Account Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Email
              </Typography>
              <Typography variant="body1">
                {currentUser.email}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Account ID
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '0.85rem' }}>
                {currentUser.uid}
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" gutterBottom>
              Documentation & Help
            </Typography>
            
            <List>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => setActiveSection('subscription')}
                  selected={activeSection === 'subscription'}
                >
                  <ListItemIcon>
                    <CreditCardIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Subscription" 
                    secondary="Manage your subscription"
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => setActiveSection('userGuide')}
                  selected={activeSection === 'userGuide'}
                >
                  <ListItemIcon>
                    <DocumentIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="User Guide" 
                    secondary="Learn how to use the app effectively"
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => setActiveSection('terms')}
                  selected={activeSection === 'terms'}
                >
                  <ListItemIcon>
                    <GavelIcon />
                  </ListItemIcon>
                  <ListItemText primary="Terms of Service" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => setActiveSection('privacy')}
                  selected={activeSection === 'privacy'}
                >
                  <ListItemIcon>
                    <SecurityIcon />
                  </ListItemIcon>
                  <ListItemText primary="Privacy Policy" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => setActiveSection('dataManagement')}
                  selected={activeSection === 'dataManagement'}
                >
                  <ListItemIcon>
                    <StorageIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Data Management" 
                    secondary="Download or delete your data"
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => setActiveSection('faq')}
                  selected={activeSection === 'faq'}
                >
                  <ListItemIcon>
                    <HelpIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="FAQ" 
                    secondary="Frequently asked questions and answers"
                  />
                </ListItemButton>
              </ListItem>
            </List>
            
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Subscription Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {isLoading ? (
                  <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      Checking...
                    </Typography>
                  </>
                ) : error ? (
                  <>
                    <InfoIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      {typeof error === 'string' && error.includes('Subscribe using') 
                        ? 'Free (Upgrade on Mobile)' 
                        : 'Verification Failed'}
                    </Typography>
                  </>
                ) : isPremium ? (
                  <>
                  <CheckIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      Active Subscription
                    </Typography>
                  </>
                ) : (
                  <>
                  <CancelIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="body1">
                      No Subscription
                </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
          
        {/* Right Panel */}
        <Grid item xs={12} md={8}>
          {renderRightPanel()}
        </Grid>
      </Grid>
      
      {/* Dialog for subscription management or success */}
      <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
        {dialogType === 'upgrade' ? (
          <>
            <DialogTitle>Subscription Successful</DialogTitle>
            <DialogContent>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
                <DialogContentText>
                  Thank you for subscribing! Your premium features are now active.
                </DialogContentText>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowDialog(false)} color="primary">
                Close
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Manage Your Subscription</DialogTitle>
            <DialogContent>
              <DialogContentText paragraph>
                Your subscription is currently active. You can manage your subscription settings below.
              </DialogContentText>
              
              {subscriptionDetails?.platform === 'web' ? (
                <List>
                  <ListItem button>
                    <ListItemIcon>
                      <CreditCardIcon />
                    </ListItemIcon>
                    <ListItemText primary="Update Payment Method" />
                  </ListItem>
                  <ListItem button>
                    <ListItemIcon>
                      <CalendarIcon />
                    </ListItemIcon>
                    <ListItemText primary="Change Billing Cycle" />
                  </ListItem>
                  <ListItem button>
                    <ListItemIcon>
                      <CancelIcon />
                    </ListItemIcon>
                    <ListItemText primary="Cancel Subscription" />
                  </ListItem>
                </List>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Your subscription was purchased through {subscriptionDetails?.platform}. 
                  Please manage your subscription through your {subscriptionDetails?.platform} account settings.
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowDialog(false)} color="primary">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Profile; 